/**
 * useStoreNavSource — the workbench-store adapter for the unified nav.
 *
 * Subscribes to the WorkspaceStore (files / active / dirty / selection / model)
 * so the nav re-renders on change, and exposes them as a {@link NavSource}.
 * Capability methods follow the source's writability: revert is always there
 * (local edits can always be undone), while create / rename / delete appear
 * only when the source is writable (presence == capability).
 */
import { useMemo } from "react";
import { useWorkspace } from "../WorkspaceContext.js";
import { useWorkspaceModel } from "../WorkspaceModelContext.js";
import {
  useActiveFile,
  useActiveTabId,
  useTabs,
  useDirtyPaths,
  useWorkspaceFiles,
  useWorkspaceSelection,
  useWorkspaceState,
} from "../WorkspaceStore.js";
import { findDeclaration } from "../sourceMap.js";
import { useModelIndexes } from "../../hooks/useModelIndexes.js";
import { useWidgetDiagnostics } from "../../hooks/useWidgetDiagnostics.js";
import { scopedFilesForId } from "../boardSourceMap.js";
import { findView, qualifiedViewName, viewRepresentation, viewsByNameOf } from "../../views/viewRepr.js";
import type { NavDiagnostic, NavDoc, NavFile, NavSource, NavViewItem } from "./NavSource.js";
import { writableNavOps } from "./writableNavOps.js";

export function useStoreNavSource(): NavSource {
  const store = useWorkspace();
  const files = useWorkspaceFiles(store);
  const active = useActiveFile(store);
  const tabs = useTabs(store);
  const activeTabId = useActiveTabId(store);
  const dirty = useDirtyPaths(store);
  const selection = useWorkspaceSelection(store);
  const { readonly, navReveal, selectedTypeName } = useWorkspaceState(store);
  const { model, resolvedModel, documents, diagnostics: modelDiagnostics } = useWorkspaceModel();
  // Model + widget-registration warnings → the nav's Problems tab (the same
  // stream the viewer's WorkspacePanel feeds it).
  const diagnostics = useWidgetDiagnostics(modelDiagnostics);
  const navDiagnostics = useMemo<readonly NavDiagnostic[]>(
    () =>
      diagnostics.map((d) => ({
        severity: d.severity,
        message: d.message,
        // Model spans are "/"-prefixed; store paths are bare — a row click
        // goes through `open`, which addresses the VFS by its own keys.
        file: d.source?.file?.startsWith("/") ? d.source.file.slice(1) : d.source?.file,
      })),
    [diagnostics],
  );
  // Interface rows resolve their owner module through the model indexes.
  const indexes = useModelIndexes(model);
  // The singleton doc tab's path = the "active document" (the Documents list
  // highlights it; switching docs re-points this one tab).
  const docTab = tabs.find((t) => t.kind === "doc");
  const activeDocPath = docTab?.kind === "doc" ? docTab.state.path : null;
  // The active view tab's name highlights that view in the Views tree.
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const activeViewName = activeTab?.kind === "view" ? activeTab.state.viewName : null;

  return useMemo<NavSource>(() => {
    const base = store.getState().base;
    const navFiles: NavFile[] = [...files.keys()].map((path) => ({
      path,
      status: dirty.has(path) ? (base.has(path) ? "modified" : "added") : undefined,
    }));

    const navDocs: NavDoc[] = documents.map((d) => ({
      name: d.name,
      path: d.path,
      space: d.space,
    }));

    const viewsByName = viewsByNameOf(resolvedModel?.views);
    const navViews: NavViewItem[] = (resolvedModel?.views ?? []).map((v) => ({
      id: v.id,
      // QUALIFIED — the id tabs/projection address a view by (viewRepr law).
      // Bare names collapsed owner-scoped twins onto one row/tab (r35).
      name: qualifiedViewName(v),
      sourceFile: v.source?.file,
      // v0.11: landed space — keeps cross-space twins distinct (D5 chip).
      space: v.space && v.space.length > 0 ? v.space.join(".") : undefined,
      // Instance parents resolve so an instance's icon matches its surface.
      representation: viewRepresentation(v, viewsByName),
    }));

    const src: NavSource = {
      files: () => navFiles,
      model: () => model,
      diagnostics: () => navDiagnostics,
      documents: () => navDocs,
      selectedDocName: () =>
        navDocs.find((d) => d.path === activeDocPath)?.name ?? null,
      // Single click opens the doc in the CM6 document surface — re-points the
      // singleton `doc` tab (fast switching, no tab pile-up).
      selectDocument: (name) => {
        const path = navDocs.find((d) => d.name === name)?.path;
        if (path) store.openTab({ kind: "doc", path });
      },
      // v0.10 views: list them, open each on its own tab, reveal on select.
      views: () => navViews,
      selectedViewName: () => activeViewName,
      openView: (name) => void store.openTab({ kind: "view", viewName: name }),
      selectView: (name) => {
        const v = findView(resolvedModel?.views, name);
        if (v?.source?.file != null) {
          store.requestReveal({ path: v.source.file, line: v.source.start.line });
        }
      },
      selection: () => ({
        moduleId: selection.moduleId,
        interfaceKey: selection.interfaceKey,
        processId: selection.activeProcessId,
        typeName: selectedTypeName,
        activeFile: active,
      }),
      // Process rows follow the universal nav grammar (same as modules):
      // single click toggles the selection highlight, double click opens the
      // process perspective. Selection is PER-TAB (it mirrors the active tab's
      // view record), so opening goes through openTab — tabs are per-process
      // (`process:${id}`), and a fresh tab's view record seeds its selection
      // from the tab state; a bare select() would write the id onto the
      // OUTGOING tab instead.
      selectProcess: (id) =>
        store.select({ activeProcessId: selection.activeProcessId === id ? null : id }),
      openProcess: (id) => void store.openTab({ kind: "process", processId: id }),
      showProcessOnBoard: (id) => store.select({ activeProcessId: id }),
      // Types follow the same grammar: single click highlights, double click
      // opens the types perspective onto that type.
      selectType: (name) =>
        store.setSelectedTypeName(selectedTypeName === name ? null : name),
      openType: (name) => store.openType(name),
      // Interfaces: single click selects (toggle) — the owner module is
      // selected too so the board keeps context; the inspector prioritises the
      // interface. Double click also flies the board to the owner.
      selectInterface: (qualifiedKey) => {
        const ownerId = indexes?.interfaceById.get(qualifiedKey)?.moduleId ?? null;
        const next = selection.interfaceKey === qualifiedKey ? null : qualifiedKey;
        store.select({
          moduleId: next ? ownerId : selection.moduleId,
          edgeId: null,
          interfaceKey: next,
        });
      },
      openInterface: (qualifiedKey) => {
        const ownerId = indexes?.interfaceById.get(qualifiedKey)?.moduleId ?? null;
        store.select({ moduleId: ownerId, edgeId: null, interfaceKey: qualifiedKey });
        if (ownerId) store.emit({ type: "boardFly", moduleId: ownerId });
      },
      // Context menu "Show on board": select + fly, opening nothing.
      showModuleOnBoard: (id) => {
        store.select({ moduleId: id, edgeId: null });
        store.emit({ type: "boardFly", moduleId: id });
      },
      // A document (`.md`) opens in the CM6 document surface (singleton `doc`
      // tab); code opens as a file tab. Dispatch on document membership.
      open: (path) =>
        navDocs.some((d) => d.path === path)
          ? void store.openTab({ kind: "doc", path })
          : store.setActiveFile(path),
      reveal: (name) => {
        const files = store.getState().files;
        const loc = findDeclaration(scopedFilesForId(files, model, name), name);
        if (loc) store.requestReveal({ path: loc.path, line: loc.line });
      },
      // Universal grammar: single click toggles the board selection…
      selectModule: (id) =>
        store.select({ moduleId: selection.moduleId === id ? null : id, edgeId: null }),
      // …double click brings into view: select, fly the board to the module,
      // and jump the editor to the declaration (findDeclaration resolves ids
      // and names alike).
      openModule: (id) => {
        store.select({ moduleId: id, edgeId: null });
        store.emit({ type: "boardFly", moduleId: id });
        const loc = findDeclaration(
          scopedFilesForId(store.getState().files, model, id),
          id,
        );
        if (loc) store.requestReveal({ path: loc.path, line: loc.line });
      },
      // Board ↔ nav linking through the workspace store. The nav rows
      // subscribe to the store per-row, so board hover never re-renders the
      // whole nav.
      hoverStore: store.hover,
      navReveal: () => navReveal,
      // Local edits can always be reverted, even on a read-only source.
      revert: (path) => store.revert(path),
      revertAll: () => store.revertAll(),
    };

    // Structural file ops only when the source is writable.
    if (!readonly) Object.assign(src, writableNavOps(store));
    return src;
  }, [store, files, active, activeDocPath, dirty, selection, selectedTypeName, readonly, model, indexes, resolvedModel, documents, navDiagnostics, navReveal, activeViewName]);
}
